import { services } from '@repo/ui/lib/api/services';
import type { GroupSubscription, GroupUsageStats, UpdateSubscriptionRequest } from '@repo/ui/lib/api/services/subscription.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/useAuth';

export interface SubscriptionLimits {
  canCreateArbre: boolean;
  canCreateAlignment: boolean;
  canCreateEpaysage: boolean;
  remainingParcours: number;
  planType: 'BASIC' | 'ADVANCED' | 'FREE';
  isTrial: boolean;
  isFreeAccount: boolean;
  isParcoursUsed: boolean;
  parcoursType: string | null;
}

/**
 * Hook pour récupérer les informations d'abonnement d'un groupe
 */
export const useGroupSubscription = (groupeId?: number) => {
  return useQuery<GroupSubscription>({
    queryKey: ['group-subscription', groupeId],
    queryFn: async () => {
      if (!groupeId) throw new Error('Groupe ID requis');
      return await services.subscription.getGroupSubscription(groupeId);
    },
    enabled: !!groupeId,
  });
};

/**
 * Hook pour récupérer les statistiques d'usage d'un groupe
 */
export const useGroupUsageStats = (groupeId?: number) => {
  return useQuery<GroupUsageStats>({
    queryKey: ['group-usage-stats', groupeId],
    queryFn: async () => {
      if (!groupeId) throw new Error('Groupe ID requis');
      return await services.subscription.getGroupUsageStats(groupeId);
    },
    enabled: !!groupeId,
  });
};

/**
 * Hook pour calculer les limites et permissions d'un groupe
 */
export const useSubscriptionLimits = (groupeId?: number): SubscriptionLimits => {
  const { data: stats } = useGroupUsageStats(groupeId);
  const auth = useAuth();

  if (!stats) {
    return {
      canCreateArbre: false,
      canCreateAlignment: false,
      canCreateEpaysage: false,
      remainingParcours: 0,
      planType: 'BASIC',
      isTrial: true,
      isFreeAccount: false,
      isParcoursUsed: false,
      parcoursType: null,
    };
  }

  const isActive = stats.groupe.subscription_status === 'ACTIVE' || stats.groupe.subscription_status === 'TRIAL';
  const isAdvanced = stats.groupe.plan_type === 'ADVANCED';
  const isFree = stats.groupe.plan_type === 'FREE';
  const isTrial = stats.groupe.subscription_status === 'TRIAL';
  const isAdmin = auth?.isAdmin;
  const isParcoursUsed = isFree ? (stats.groupe.free_parcours_used ?? false) : false;
  const parcoursType = isFree ? (stats.groupe.free_parcours_type ?? null) : null;

  if (isFree) {
    const canCreateParcours = isAdmin || (isActive && !isParcoursUsed);
    const freeParcours = canCreateParcours ? 1 : 0;
    return {
      canCreateArbre: canCreateParcours,
      canCreateAlignment: canCreateParcours,
      canCreateEpaysage: canCreateParcours,
      remainingParcours: freeParcours,
      planType: 'FREE',
      isTrial,
      isFreeAccount: true,
      isParcoursUsed,
      parcoursType,
    };
  }

  const remainingParcours = stats.remainingParcours;

  return {
    canCreateArbre: isAdmin || (isActive && remainingParcours > 0),
    canCreateAlignment: isAdmin || (isActive && isAdvanced && remainingParcours > 0),
    canCreateEpaysage: isAdmin || (isActive && isAdvanced && remainingParcours > 0),
    remainingParcours,
    planType: stats.groupe.plan_type,
    isTrial,
    isFreeAccount: false,
    isParcoursUsed: false,
    parcoursType: null,
  };
};

/**
 * Hook pour mettre à jour le plan d'un groupe
 */
export const useUpdateGroupPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      groupeId,
      updateData
    }: {
      groupeId: number;
      updateData: UpdateSubscriptionRequest;
    }) => {
      return await services.subscription.updateGroupPlan(groupeId, updateData);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['group-subscription', variables.groupeId] });
      queryClient.invalidateQueries({ queryKey: ['group-usage-stats', variables.groupeId] });
    },
  });
};

/**
 * Hook pour remettre à zéro l'usage mensuel
 */
export const useResetMonthlyUsage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupeId: number) => {
      return await services.subscription.resetMonthlyUsage(groupeId);
    },
    onSuccess: (_, groupeId) => {
      queryClient.invalidateQueries({ queryKey: ['group-usage-stats', groupeId] });
      queryClient.invalidateQueries({ queryKey: ['group-subscription', groupeId] });
    },
  });
};

/**
 * Hook pour mettre à jour l'abonnement Stripe
 */
export const useUpdateStripeSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      groupeId,
      updateData
    }: {
      groupeId: number;
      updateData: {
        sub_id: string;
        customer_id: string;
        forfait_id: number;
        date_echeance: string;
        date_subscribed?: string;
      };
    }) => {
      return await services.subscription.updateStripeSubscription(groupeId, updateData);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['group-subscription', variables.groupeId] });
    },
  });
};

/**
 * Hook pour annuler un abonnement
 */
export const useCancelSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupeId: number) => {
      return await services.subscription.cancelSubscription(groupeId);
    },
    onSuccess: (_, groupeId) => {
      queryClient.invalidateQueries({ queryKey: ['group-subscription', groupeId] });
      queryClient.invalidateQueries({ queryKey: ['group-usage-stats', groupeId] });
    },
  });
};

/**
 * Hook pour activer/désactiver le mode limité
 */
export const useToggleStrippedStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      groupeId,
      isStripped
    }: {
      groupeId: number;
      isStripped: boolean;
    }) => {
      return await services.subscription.toggleStrippedStatus(groupeId, isStripped);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['group-subscription', variables.groupeId] });
    },
  });
};
